import React, { useState } from 'react';
import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import ComingSoonModal from '../components/landing/ComingSoonModal';

const BRAND = {
  black: '#0A0A0A',
  bg: '#FFFFFF',
  surface: '#FFFFFF',
  border: '#E5E7EB',
  subtle: '#6B7280',
  green: '#0FC862',
  greenHover: '#14F195',
  error: '#DC2626',
};

export default function Login() {
  const nav = useNavigate();
  const loc = useLocation();
  const { login, isAuthed, user } = useAuth();
  const params = new URLSearchParams(loc.search);
  const redirect = params.get('redirect') || '';
  const isSignup = params.get('signup') === '1';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showDemo, setShowDemo] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);

  // If already authed, redirect by role / redirect param.
  if (isAuthed && user) {
    const dest = redirect || (user.role === 'admin' ? '/admin' : '/app');
    return <Navigate to={dest} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      const dest = redirect || (u.role === 'admin' ? '/admin' : '/app');
      nav(dest, { replace: true });
    } catch (err) {
      setError(err?.message || 'Could not sign in');
    } finally {
      setBusy(false);
    }
  };

  const useDemoCreds = () => {
    setEmail('demo@clasifi.ca');
    setPassword('ClasifiDemo!2026');
  };

  return (
    <div
      data-testid="login-page"
      style={{
        minHeight: '100vh', backgroundColor: BRAND.bg, display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 24,
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
      }}
    >
      <div style={{ width: '100%', maxWidth: 420 }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', marginBottom: 32 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, backgroundColor: BRAND.green, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 18, boxShadow: `0 8px 24px -8px ${BRAND.green}` }}>C</div>
          <span style={{ color: BRAND.black, fontWeight: 800, fontSize: 20, letterSpacing: '-0.02em' }}>Clasifi</span>
        </Link>

        <h1 style={{ color: BRAND.black, fontSize: 30, fontWeight: 800, letterSpacing: '-0.02em', margin: 0 }}>
          {isSignup ? 'Start your free trial' : 'Welcome back'}
        </h1>
        <p style={{ color: BRAND.subtle, marginTop: 8, marginBottom: 28, fontSize: 15 }}>
          {isSignup
            ? 'Signup is coming soon. For early access, email support@clasifi.ca.'
            : 'Sign in to continue to Clasifi.'}
        </p>

        {isSignup && (
          <div data-testid="signup-banner" style={{ padding: 12, border: `1px solid ${BRAND.border}`, borderRadius: 10, backgroundColor: '#F9FAFB', color: BRAND.subtle, fontSize: 13, marginBottom: 20 }}>
            Signup coming soon — contact <a href="mailto:support@clasifi.ca" style={{ color: BRAND.green, fontWeight: 600 }}>support@clasifi.ca</a> to onboard. Existing users can sign in below.
          </div>
        )}

        <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: BRAND.black }}>
            Email
            <input
              data-testid="login-email"
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              required autoComplete="email"
              style={inputStyle}
            />
          </label>
          <label style={{ fontSize: 13, fontWeight: 600, color: BRAND.black }}>
            Password
            <input
              data-testid="login-password"
              type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              required autoComplete="current-password"
              style={inputStyle}
            />
          </label>

          {error && (
            <div data-testid="login-error" style={{ color: BRAND.error, backgroundColor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '10px 12px', fontSize: 13 }}>
              {error}
            </div>
          )}

          <button
            data-testid="login-submit"
            type="submit" disabled={busy}
            style={{
              marginTop: 4, padding: '12px 16px', backgroundColor: busy ? '#9CA3AF' : BRAND.green,
              color: 'white', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 15,
              cursor: busy ? 'not-allowed' : 'pointer',
              boxShadow: busy ? 'none' : `0 10px 30px -10px ${BRAND.green}`,
            }}
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </button>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, marginTop: 4 }}>
            <button type="button" data-testid="forgot-password-link" onClick={() => setModal('forgot')} style={linkBtn}>
              Forgot password?
            </button>
            <Link to="/login?signup=1" style={{ color: BRAND.green, textDecoration: 'none', fontWeight: 600 }}>
              Start free trial
            </Link>
          </div>
        </form>

        <div style={{ marginTop: 28, padding: 14, backgroundColor: '#F9FAFB', border: `1px solid ${BRAND.border}`, borderRadius: 10, fontSize: 13 }}>
          <button type="button" data-testid="demo-toggle" onClick={() => setShowDemo((s) => !s)} style={{ ...linkBtn, fontWeight: 700, color: BRAND.black }}>
            {showDemo ? '− Hide demo credentials' : '+ Show demo credentials'}
          </button>
          {showDemo && (
            <div style={{ marginTop: 10, color: BRAND.subtle, lineHeight: 1.7 }}>
              <div><strong style={{ color: BRAND.black }}>Demo user:</strong> demo@clasifi.ca / ClasifiDemo!2026</div>
              <div><strong style={{ color: BRAND.black }}>Admin:</strong> admin@clasifi.ca / ClasifiAdmin!2026</div>
              <button data-testid="use-demo-btn" onClick={useDemoCreds} type="button" style={{ marginTop: 8, padding: '6px 12px', backgroundColor: 'white', border: `1px solid ${BRAND.border}`, borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: BRAND.black }}>
                Fill demo credentials
              </button>
            </div>
          )}
        </div>
      </div>

      {modal === 'forgot' && (
        <ComingSoonModal
          title="Password reset coming soon"
          body="Email support@clasifi.ca and we'll get you back in."
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

const inputStyle = {
  display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 6,
  padding: '11px 13px', borderRadius: 10, border: `1px solid ${BRAND.border}`,
  fontSize: 15, color: BRAND.black, backgroundColor: 'white', outline: 'none',
};
const linkBtn = { background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', padding: 0, fontSize: 13 };
